import { Box, Checkbox, Chip, Collapse, FormControlLabel, Radio, RadioGroup } from "@material-ui/core"
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown"
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp"
import { contains } from "ramda"
import { useMemo, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"

import type { SearchFilter } from "../../../redux"
import { Header5, LinkButton } from "../../atoms"

import "./filter-category.styles.scss"
import { useTranslation } from "react-i18next"

export interface IFilterCategoryProps {
  summaryTitle: string
  filterOptions: SearchFilter[]
  selectedFilters: string[]
  disabled?: boolean
  radioSelection?: boolean
  onSelection: (selected: boolean, filterOption: SearchFilter) => void
}

export function FilterCategory(props: IFilterCategoryProps): ReactElement {
  const { summaryTitle, filterOptions, selectedFilters, onSelection, disabled, radioSelection } = props
  const { t } = useTranslation("general")
  const [showAll, setShowAll] = useState<boolean>(false)
  const [filterExpanded, setFilterExpanded] = useState<boolean>(true)

  const trimmedFilterOptions: SearchFilter[] = useMemo<SearchFilter[]>(() => {
    if (showAll) return filterOptions
    return filterOptions.slice(0, 5)
  }, [filterOptions, showAll])

  const activeFiltersCount: number = useMemo<number>(() => {
    return selectedFilters ? selectedFilters.length : 0
  }, [selectedFilters])

  const shouldTrimOptions: boolean = useMemo<boolean>(() => {
    return filterOptions.length > 5
  }, [filterOptions])

  function handleOptionSelected(
    option: SearchFilter
  ): (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void {
    return (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onSelection(checked, option)
    }
  }

  function optionIsSelected(option: string): boolean {
    if (!selectedFilters) return false
    return contains(option, selectedFilters)
  }

  function FilterHeader(): ReactElement {
    return (
      <Box className="filter-header">
        <Header5>{summaryTitle}</Header5>
        <Chip
          label={activeFiltersCount}
          className={`count-chip ${activeFiltersCount === 0 ? "empty" : ""}`}
          color="primary"
        />
        <Box onClick={() => setFilterExpanded(!filterExpanded)}>
          <KeyboardArrowUp className={`expand-icon ${filterExpanded ? "" : "inactive"}`} />
        </Box>
      </Box>
    )
  }

  function CheckboxFilters(): ReactElement {
    return (
      <>
        {trimmedFilterOptions.map((filterOption: SearchFilter) => (
          <FormControlLabel
            key={filterOption.name}
            label={`${filterOption.name} (${filterOption.count})`}
            control={
              <Checkbox
                color="primary"
                id={filterOption.name}
                checked={optionIsSelected(filterOption.name)}
                disabled={disabled}
                onChange={handleOptionSelected(filterOption)}
              />
            }
          />
        ))}
        {shouldTrimOptions && (
          <LinkButton onClick={() => setShowAll(!showAll)} className="filter-expand-button">
            {showAll ? (
              <>
                <KeyboardArrowUp /> {t("showLess")}
              </>
            ) : (
              <>
                <KeyboardArrowDown />
                {t("showMore")}
              </>
            )}
          </LinkButton>
        )}
      </>
    )
  }

  function RadioFilters(): ReactElement {
    return (
      <>
        <RadioGroup>
          {trimmedFilterOptions
            .filter((filterOption: SearchFilter) => filterOption.count > 0)
            .map((filterOption: SearchFilter) => (
              <FormControlLabel
                key={filterOption.name}
                label={`${filterOption.name} (${filterOption.count})`}
                control={
                  <Radio
                    color="primary"
                    id={filterOption.name}
                    checked={optionIsSelected(filterOption.name)}
                    disabled={disabled}
                    onChange={handleOptionSelected(filterOption)}
                  />
                }
              />
            ))}
        </RadioGroup>
        {shouldTrimOptions && (
          <LinkButton onClick={() => setShowAll(!showAll)} className="filter-expand-button">
            {showAll ? (
              <>
                <KeyboardArrowUp /> {t("showLess")}
              </>
            ) : (
              <>
                <KeyboardArrowDown />
                {t("showMore")}
              </>
            )}
          </LinkButton>
        )}
      </>
    )
  }

  return filterOptions.length > 0 ? (
    <Box className="filter-category">
      <FilterHeader />
      <Collapse in={filterExpanded}>{radioSelection ? <RadioFilters /> : <CheckboxFilters />}</Collapse>
    </Box>
  ) : (
    <></>
  )
}
